import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { PageHeader, SectionCard, EmptyState } from '../ui';
import { paymentService, type ManualJob, type AuditGroup, type JobStats } from '../../services/paymentService';

// Thanh thống kê job ở đầu trang: cần xử lý tay, đã xử lý, thành công, thất bại.
// Tự tải lại mỗi khi `refreshKey` đổi (sau khi admin xử lý tay / reset / tải lại).
const JobStatsBar: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [stats, setStats] = useState<JobStats | null>(null);
  useEffect(() => {
    let alive = true;
    paymentService
      .jobStats()
      .then((s) => {
        if (alive) setStats(s);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  // `today` = phát sinh hôm nay (giờ VN) — hiển thị dòng "+xx hôm nay" nhỏ dưới số lớn.
  const cells: Array<{ label: string; value: number; today: number; color: string }> = [
    { label: 'Cần xử lý tay', value: stats?.pendingManual ?? 0, today: stats?.pendingManualToday ?? 0, color: '#ed6c02' },
    { label: 'Đã xử lý', value: stats?.processed ?? 0, today: stats?.processedToday ?? 0, color: '#0288d1' },
    { label: 'Thành công', value: stats?.success ?? 0, today: stats?.successToday ?? 0, color: '#2e7d32' },
    { label: 'Thất bại', value: stats?.failed ?? 0, today: stats?.failedToday ?? 0, color: '#d32f2f' },
  ];

  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {cells.map((c) => (
        <Box
          key={c.label}
          sx={{
            px: 2,
            py: 1,
            minWidth: 120,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {c.label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: c.color, lineHeight: 1.2 }}>
            {stats ? c.value.toLocaleString() : '—'}
          </Typography>
          {stats && c.today > 0 ? (
            <Typography variant="caption" sx={{ color: c.color, opacity: 0.85, fontWeight: 600, display: 'block', mt: 0.25 }}>
              +{c.today.toLocaleString()} hôm nay
            </Typography>
          ) : null}
        </Box>
      ))}
    </Stack>
  );
};

// Job "cần xử lý tay" (pending_manual): lỗi device/bất thường đã cạn số lần thử.
// Admin xem input giải mã (email|password|recovery), tự lấy link, rồi nhập kết quả:
//   - Thành công + link  → Worker gửi link cho user (đợt 2), KHÔNG hoàn tiền.
//   - Thất bại           → Worker hoàn point dòng đó cho user.
//   - Job API (partner) đã failed: partner đã nhận kết quả (chỉ trả 1 lần) → "Lưu kết quả":
//     KHÔNG gửi, KHÔNG trừ lại; partner gửi lại email đó thì trả ngay link đã lưu (tính tiền).
// ⚠ Trang này hiển thị mật khẩu bản rõ — chỉ Admin, dùng trên máy tin cậy.

/** Job API đã failed: chỉ lưu kết quả, không gửi lại (khớp setJobManualResult phía Worker). */
const saveOnly = (j: ManualJob): boolean => j.source === 'partner' && j.status === 'failed';

const fmtTime = (ms: number): string => {
  if (!ms) return '—';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export const BotManualJobs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null); // jobId đang xử lý
  const [jobs, setJobs] = useState<ManualJob[]>([]);
  const [tab, setTab] = useState(0);
  const [includeFailed, setIncludeFailed] = useState(false);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [statsKey, setStatsKey] = useState(0); // bump để thanh thống kê tải lại
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const notify = (msg: string, sev: 'success' | 'error') => setToast({ msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { jobs } = await paymentService.listManualJobs({ includeFailed, limit: 200 });
      setJobs(jobs);
      setStatsKey((k) => k + 1);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [includeFailed]);

  useEffect(() => {
    load();
  }, [load]);

  const requeue = async (job: ManualJob) => {
    if (!window.confirm(`Reset job "${job.email}" về hàng đợi để chạy lại từ đầu?\n(Số lần thử về 0, xoá kết quả cũ. Không hoàn/không trừ tiền.)`)) return;
    setBusy(job.jobId);
    try {
      await paymentService.requeueManualJob(job.jobId);
      notify('Đã reset về hàng đợi — runner sẽ nhận lại.', 'success');
      setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
      setStatsKey((k) => k + 1);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  };

  const resolve = async (job: ManualJob, status: 'success' | 'failed') => {
    const link = (links[job.jobId] ?? '').trim();
    if (status === 'success' && !link) return notify('Nhập link kết quả trước khi đánh dấu thành công', 'error');
    // failed → success = khôi phục đơn đã hoàn tiền → sẽ TRỪ LẠI point của khách.
    // (Job API đã failed thì chỉ lưu, không gửi/không trừ → khỏi confirm.)
    if (status === 'success' && job.status === 'failed' && !saveOnly(job)) {
      if (!window.confirm('Đơn này đã failed + đã hoàn tiền. Gửi link sẽ TRỪ LẠI point của khách. Tiếp tục?')) return;
    }
    setBusy(job.jobId);
    try {
      const r = await paymentService.setJobManualResult(job.jobId, status, status === 'success' ? link : undefined);
      notify(
        status === 'failed'
          ? 'Đã đánh dấu thất bại + hoàn point.'
          : r.saved
            ? 'Đã lưu kết quả — trả ngay nếu khách gửi lại email này.'
            : r.recharged
            ? 'Đã gửi link cho khách + trừ lại point.'
            : 'Đã gửi link cho khách.',
        'success',
      );
      setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
      setStatsKey((k) => k + 1);
      setLinks((prev) => {
        const next = { ...prev };
        delete next[job.jobId];
        return next;
      });
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Bot Jobs" />

      <JobStatsBar refreshKey={statsKey} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Cần xử lý tay" />
        <Tab label="Tất cả job" />
      </Tabs>

      {tab === 1 ? (
        <AllJobsPanel />
      ) : (
      <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 1 }}>
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
          Tải lại
        </Button>
        <FormControlLabel
          control={<Switch checked={includeFailed} onChange={(e) => setIncludeFailed(e.target.checked)} />}
          label="Kèm job đã failed"
        />
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <SectionCard>
          {jobs.length === 0 ? (
            <EmptyState title="Không có job nào cần xử lý tay" />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Input (email|password|recovery)</TableCell>
                    <TableCell align="center">Lần thử</TableCell>
                    <TableCell>Lỗi</TableCell>
                    <TableCell>Lúc gửi</TableCell>
                    <TableCell sx={{ minWidth: 360 }}>Nhập kết quả</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((j) => (
                    <TableRow key={j.jobId}>
                      <TableCell>{j.email}</TableCell>
                      <TableCell>
                        <TextField
                          value={j.input}
                          size="small"
                          fullWidth
                          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                        />
                      </TableCell>
                      <TableCell align="center">{j.attempts}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={j.status === 'failed' ? '✖ đã failed (đã hoàn tiền)' : '⏳ chờ xử lý'}
                          color={j.status === 'failed' ? 'default' : 'warning'}
                          sx={{ mb: 0.5 }}
                        />
                        <br />
                        <Chip size="small" variant="outlined" label={j.messageCode ?? '—'} />
                        {j.message ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {j.message}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{fmtTime(j.requestedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <TextField
                            label="Link kết quả"
                            placeholder="https://..."
                            value={links[j.jobId] ?? ''}
                            onChange={(e) => setLinks((p) => ({ ...p, [j.jobId]: e.target.value }))}
                            size="small"
                            fullWidth
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={busy === j.jobId}
                              title={
                                saveOnly(j)
                                  ? 'Job API đã trả failed cho khách — chỉ lưu link, trả ngay nếu khách gửi lại email này'
                                  : j.status === 'failed'
                                    ? 'Gửi link cho khách + TRỪ LẠI point (đơn đã hoàn tiền trước đó)'
                                    : 'Gửi link cho khách'
                              }
                              onClick={() => resolve(j, 'success')}
                            >
                              {saveOnly(j) ? 'Lưu kết quả' : 'Thành công (gửi link)'}
                            </Button>
                            {/* failed = đã trả failed + ĐÃ hoàn tiền → không hiện nút hoàn lại. */}
                            {j.status !== 'failed' ? (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={busy === j.jobId}
                                onClick={() => resolve(j, 'failed')}
                              >
                                Thất bại (hoàn tiền)
                              </Button>
                            ) : null}
                            {j.status === 'pending_manual' ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                startIcon={<ReplayRoundedIcon />}
                                disabled={busy === j.jobId}
                                onClick={() => requeue(j)}
                              >
                                Reset về hàng đợi
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      )}
      </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

const STATUS_OPTIONS = ['', 'queued', 'running', 'success', 'failed', 'pending_manual', 'rejected'] as const;
const STATUS_LABEL: Record<string, string> = {
  '': 'Tất cả trạng thái',
  queued: 'Chờ (queued)',
  running: 'Đang chạy',
  success: 'Thành công',
  failed: 'Thất bại',
  pending_manual: 'Cần xử lý tay',
  rejected: 'Bị từ chối',
};
const statusColor = (s: string): 'success' | 'error' | 'warning' | 'info' | 'default' =>
  s === 'success' ? 'success' : s === 'failed' || s === 'rejected' ? 'error' : s === 'pending_manual' ? 'warning' : s === 'running' ? 'info' : 'default';

const PAGE = 100;

// Một nhóm email (gộp mọi lần thử): dòng chính là lần MỚI NHẤT; bấm mũi tên để bung
// xem toàn bộ lịch sử các lần thử.
const JobGroupRow: React.FC<{ group: AuditGroup }> = ({ group }) => {
  const [open, setOpen] = useState(false);
  const latest = group.history[0];
  const sc = statusColor(group.latestStatus);
  const badgeBg = sc === 'default' ? 'grey.500' : `${sc}.main`;
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40, pr: 0 }}>
          {group.attempts > 1 ? (
            <Box
              onClick={() => setOpen((v) => !v)}
              title="Bấm để xem lịch sử các lần thử"
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: badgeBg,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { opacity: 0.85 },
              }}
            >
              {group.attempts}
            </Box>
          ) : null}
        </TableCell>
        <TableCell>{group.email}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{group.requester || '—'}</TableCell>
        <TableCell sx={{ maxWidth: 260, verticalAlign: 'top' }}>
          <Chip size="small" label={latest?.messageCode ?? group.latestStatus} color={statusColor(group.latestStatus)} sx={{ maxWidth: '100%' }} />
          {latest?.url ? (
            <Box sx={{ mt: 0.5, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Link href={latest.url} target="_blank" rel="noopener" variant="caption" title={latest.url}>
                {latest.url}
              </Link>
            </Box>
          ) : latest?.message ? (
            <Typography variant="caption" color="text.secondary" display="block" noWrap title={latest.message} sx={{ maxWidth: 260 }}>
              {latest.message}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          <Typography variant="caption">{fmtTime(group.latestAt)}</Typography>
        </TableCell>
      </TableRow>
      {group.attempts > 1 ? (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, border: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ my: 1, ml: 4 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Lịch sử {group.attempts} lần thử (mới nhất trước)
                </Typography>
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Trạng thái</TableCell>
                      <TableCell>Serial device</TableCell>
                      <TableCell>IMEI</TableCell>
                      <TableCell>Output</TableCell>
                      <TableCell>Lúc gửi</TableCell>
                      <TableCell>Hoàn tất</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.history.map((j) => (
                      <TableRow key={j.jobId}>
                        <TableCell sx={{ maxWidth: 240, verticalAlign: 'top' }}>
                          <Chip size="small" label={j.messageCode ?? j.status} color={statusColor(j.status)} sx={{ maxWidth: '100%' }} />
                          {j.message ? (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap title={j.message} sx={{ maxWidth: 240 }}>
                              {j.message}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {j.serialNo || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {j.imei || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 320, maxWidth: 520, verticalAlign: 'top' }}>
                          {j.url ? (
                            <Box sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-all' }}>
                              <Link href={j.url} target="_blank" rel="noopener" variant="caption" title={j.url}>
                                {j.url}
                              </Link>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtTime(j.requestedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtTime(j.completedAt ?? 0)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

// Tab tra cứu TOÀN BỘ job, GỘP THEO EMAIL: mỗi email một dòng (người gửi + trạng thái
// lần mới nhất + số lần thử), bung ra xem toàn bộ lịch sử. Không hiển thị raw input
// (email|password|recovery) để tránh rò rỉ.
const AllJobsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<AuditGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  const [requester, setRequester] = useState('');
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { groups, total } = await paymentService.listAllJobs({
        limit: PAGE,
        offset,
        ...(status ? { status } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(requester.trim() ? { requester: requester.trim() } : {}),
      });
      setGroups(groups);
      // total < 0: backend bỏ đếm ở trang sau (tối ưu) → giữ tổng đã biết.
      if (total >= 0) setTotal(total);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [status, email, requester, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Đổi filter → về trang đầu.
  const onStatus = (v: string) => {
    setOffset(0);
    setStatus(v);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
          Tải lại
        </Button>
        <TextField
          select
          label="Trạng thái (lần mới nhất)"
          size="small"
          value={status}
          onChange={(e) => onStatus(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s || 'all'} value={s}>
              {STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Lọc theo email"
          size="small"
          value={email}
          placeholder="email chính xác"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setOffset(0);
              load();
            }
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          label="Lọc theo người gửi"
          size="small"
          value={requester}
          placeholder="telegram id, hoặc tên/username"
          onChange={(e) => setRequester(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setOffset(0);
              load();
            }
          }}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {err ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <SectionCard>
          {groups.length === 0 ? (
            <EmptyState title="Không có email khớp bộ lọc" />
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }}>Lần</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Người gửi</TableCell>
                      <TableCell>Trạng thái mới nhất</TableCell>
                      <TableCell>Lần mới nhất</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((g) => (
                      <JobGroupRow key={g.email} group={g} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {offset + 1}–{offset + groups.length} / {total} email
                </Typography>
                <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                  Trước
                </Button>
                <Button size="small" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
                  Sau
                </Button>
              </Stack>
            </>
          )}
        </SectionCard>
      )}
    </Box>
  );
};
